import WhiteLabelLogo from "@/components/_module/loading/white-label-logo";
import CardFailedPage from "@/components/pages/card/failed";
import PageLoader from "@/components/ui/PageLoader";
import PageTransition from "@/components/ui/PageTransition";
import { useAuth } from "@/context/auth-context";
import useGetBankAccounts from "@/features/bank-accounts/useGetBankAccounts";
import { useGetAccountBalance } from "@/features/transactions/useGetAccountBalance";
import useTransferOut from "@/features/transactions/useTransferOut";
import { cn } from "@/lib/utils";
import { useLoaderData, useNavigate, useParams } from "@tanstack/react-router";
import WithdrawSuccessPage from "../../../../components/pages/withdraw/withdrawSuccess";
import { Portal } from "../../deposit/amount/review-deposit/ReviewDepositContainer";
import SelectWithdrawAmount from "./_components/SelectAmount";

const WithdrawAmountPage = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const { accountId } = useParams({ from: "/main/withdraw/$accountId/amount" });
	const { location_id } = useLoaderData({ from: "__root__" });
	const bankAccountsQuery = useGetBankAccounts({
		guestId: user.id,
		locationId: location_id,
	});
	const transferOutMutation = useTransferOut();
	const balanceQuery = useGetAccountBalance({
		guestId: user.id,
		locationId: location_id,
	});

	const submitPayment = (amount: number) => {
		transferOutMutation.mutate({
			guestId: user.id,
			accountId,
			amount: amount,
			locationId: location_id,
		});
	};

	if (transferOutMutation.status === "success") {
		return (
			<div className="kashu-container">
				<WithdrawSuccessPage />
			</div>
		);
	}

	if (transferOutMutation.status === "error") {
		return (
			<CardFailedPage
				errorMessage={transferOutMutation.error.message}
				onTryAgainClick={() => {
					navigate({ to: "/main/withdraw" });
				}}
			/>
		);
	}

	return (
		<>
			<PageLoader query={bankAccountsQuery}>
				{(bankAccounts) => {
					const selectedAccount = bankAccounts.find(
						(account) => account.id.toString() === accountId,
					);
					if (!selectedAccount) {
						return (
							<CardFailedPage
								errorMessage="We couldn't find the selected account. Please go back and try again."
								onTryAgainClick={() => {
									navigate({ to: "/main/withdraw" });
								}}
							/>
						);
					}

					return (
						<div>
							<PageTransition>
								<PageLoader query={balanceQuery}>
									{(balanceData) => {
										return (
											<SelectWithdrawAmount
												balance={balanceData.balance}
												selectedAccount={selectedAccount}
												onClose={() => {
													navigate({
														to: "/main/withdraw",
													});
												}}
												onBack={() => {
													history.back();
												}}
												onContinue={(amount) => {
													submitPayment(amount);
												}}
											/>
										);
									}}
								</PageLoader>
							</PageTransition>
							<Portal>
								<div
									className={cn(
										"fixed inset-0 top-0 left-0 w-full h-full flex items-center justify-center z-50 transition-opacity duration-300",
										transferOutMutation.status === "pending"
											? "opacity-100 pointer-events-auto"
											: "opacity-0 pointer-events-none",
									)}
									data-testid="loading-overlay"
								>
									<div className="absolute inset-0 bg-black opacity-60 transition-opacity duration-300" />
									<div className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
										<WhiteLabelLogo />
									</div>
								</div>
							</Portal>
						</div>
					);
				}}
			</PageLoader>
		</>
	);
};

export default WithdrawAmountPage;
